import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { formatError } from '@/core/utils/error.formatter'

type Kind = 'info' | 'success' | 'error'

interface Toast {
  id: number
  kind: Kind
  title: string
  body?: string
}

interface ToastApi {
  show: (title: string, body?: string, kind?: Kind) => void
  success: (title: string, body?: string) => void
  /** Accepts a thrown value directly so callers don't format errors twice. */
  error: (error: unknown, fallbackTitle?: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (title: string, body?: string, kind: Kind = 'info') => {
      const id = nextId++
      setToasts((list) => [...list, { id, kind, title, body }])
      // Errors stay longer: they usually carry something worth reading.
      setTimeout(() => dismiss(id), kind === 'error' ? 8000 : 4000)
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, body) => show(title, body, 'success'),
      error: (error, fallbackTitle) => {
        const e = formatError(error)
        show(fallbackTitle ?? e.title, e.hint ? `${e.message} ${e.hint}` : e.message, 'error')
      },
    }),
    [show],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="toast" data-kind={t.kind} onClick={() => dismiss(t.id)}>
            <strong>{t.title}</strong>
            {t.body && <div className="small muted">{t.body}</div>}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
